import {useEffect} from 'react';

/**
 * User-activity events that reset the idle timer. Background work (RTK Query
 * polling, etc.) must NOT count as activity, so this tracks DOM input events
 * rather than dispatched Redux actions. See this.i wqz7k3mnbp.
 */
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
] as const;

/**
 * Invoke `onIdle` after `timeoutMs` of no user activity. Any activity event
 * restarts the countdown. Inert while `enabled` is false. Listeners and the
 * pending timer are torn down on unmount or when inputs change.
 */
export function useIdleTimeout(
  enabled: boolean,
  timeoutMs: number,
  onIdle: () => void,
): void {
  useEffect(() => {
    if (!enabled) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const reset = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(onIdle, timeoutMs);
    };

    ACTIVITY_EVENTS.forEach(evt =>
      window.addEventListener(evt, reset, {passive: true}),
    );
    reset();

    return () => {
      if (timer !== undefined) clearTimeout(timer);
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, reset));
    };
  }, [enabled, timeoutMs, onIdle]);
}
